import { ArrowLeft, InfoIcon } from 'lucide-preact';

import { useCustomerLoyalty } from '@/context/LoyaltyContext';
import { useNavigation } from '@/context/NavigationContext';
import { usePlatformHandlers } from '@/context/PlatformContext';
import { useWidgetConfig } from '@/context/WidgetConfigContext';
import { COLORS } from '@/helpers/constants/theme';
import { formatNumberWithCommas } from '@/helpers/functions/number';
import { useHoverBackground } from '@/hooks/useHoverBackground';
import { useRedemption } from '@/hooks/useRedemption';
import { E_AppRoutes } from '@/types/Routes';

export const Redemption = () => {
  const { customerLoyalty, pointsToRedeem } = useCustomerLoyalty();
  const { redeemFlexiblePoints } = usePlatformHandlers();
  const { navigateTo } = useNavigation();
  const { isRedeeming, applyRedemption } = useRedemption();
  const { theme, fullRedemptionOnly } = useWidgetConfig();
  const brandColor = theme?.brandColor;

  const { backgroundColor = COLORS.BRAND_SECONDARY, textColor = COLORS.TEXT_INVERSE } =
    theme?.secondaryButton ?? {};

  const {
    backgroundColor: hoverBackground,
    hoverHandlers,
    isHovered,
  } = useHoverBackground({
    baseColor: backgroundColor,
  });

  const {
    backgroundColor: primaryButtonBackgroundColor = COLORS.BRAND_PRIMARY,
    textColor: primaryButtonTextColor = COLORS.TEXT_INVERSE,
  } = theme?.primaryButton ?? {};

  const {
    backgroundColor: primaryButtonHoverBackground,
    hoverHandlers: primaryButtonHoverHandlers,
  } = useHoverBackground({
    baseColor: primaryButtonBackgroundColor,
  });

  if (!customerLoyalty) return null;
  const { programs, basicInfo } = customerLoyalty;
  const { flexiblePoints } = programs;
  const { exchangeRate, pointsBalanceTitleEn } = flexiblePoints;
  const currency = basicInfo?.currency;

  const cashValue = pointsToRedeem / exchangeRate;

  const handleConfirm = async () => {
    const customerId = basicInfo?.enableId;
    if (!redeemFlexiblePoints || !customerId) return;

    await applyRedemption(() =>
      redeemFlexiblePoints({
        customerId,
        pointsAmount: pointsToRedeem,
      }),
    );
  };

  const goBack = () =>
    navigateTo(fullRedemptionOnly ? E_AppRoutes.REGISTERED_CUSTOMER : E_AppRoutes.FLEXIBLE_POINTS);

  const formattedAccountCredit = formatNumberWithCommas(cashValue);
  const formattedPointsToRedeem = formatNumberWithCommas(pointsToRedeem);

  return (
    <div className='py-4'>
      <button
        onClick={() => goBack()}
        className='mb-6 flex items-center space-x-2 border-0 bg-transparent text-gray-600 outline-0 transition-colors hover:text-gray-900'
      >
        <ArrowLeft className='h-4 w-4' />
        <span className='text-sm'>Back</span>
      </button>

      <div className='mb-8 text-center'>
        <div
          className='mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full'
          style={
            brandColor
              ? { backgroundColor: brandColor }
              : {
                  background:
                    'linear-gradient(to bottom right, rgb(74, 222, 128), rgb(16, 185, 129))',
                }
          }
        >
          <span className='text-3xl'>💰</span>
        </div>

        <h3 className='mb-2'>Confirm Redemption</h3>
        <h5>
          You're about to redeem{' '}
          <span className='font-semibold'>
            {formattedPointsToRedeem} {pointsBalanceTitleEn}
          </span>{' '}
          for{' '}
          <span className='font-semibold'>
            {currency} {formattedAccountCredit}
          </span>
        </h5>
      </div>

      <div className='mb-6 rounded-lg border border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 p-4'>
        <div className='flex items-center justify-between'>
          <div>
            <h2>
              {formattedPointsToRedeem} {pointsBalanceTitleEn}
            </h2>
            <h5>Account Credit</h5>
          </div>
          <div className='text-right'>
            <span className='text-lg font-bold'>
              {currency} {formattedAccountCredit}
            </span>
          </div>
        </div>
      </div>

      <div className='mb-4 space-y-2'>
        <div className='flex items-center justify-around gap-2 rounded-md bg-blue-100 px-3 py-2 text-sm text-blue-700'>
          <InfoIcon className='mt-[2px] h-10 w-10' />
          <h5 className='!text-blue-500'>
            by clicking redeem, this discount of ({formattedAccountCredit} {currency}) will be
            automatically applied to your cart
          </h5>
        </div>
      </div>

      <div className='space-y-3'>
        <button
          {...primaryButtonHoverHandlers}
          onClick={handleConfirm}
          disabled={isRedeeming}
          className={`w-full rounded-lg px-4 py-3 font-semibold transition-colors ${
            isRedeeming && 'cursor-not-allowed opacity-60'
          }`}
          style={{
            backgroundColor: isRedeeming ? COLORS.DISABLED_DARK : primaryButtonHoverBackground,
            color: primaryButtonTextColor,
          }}
        >
          {isRedeeming ? 'Processing...' : `Redeem ${pointsBalanceTitleEn}`}
        </button>

        <button
          {...hoverHandlers}
          onClick={() => goBack()}
          disabled={isRedeeming}
          className={`w-full rounded-lg border px-4 py-3 transition-colors ${
            isRedeeming && 'cursor-not-allowed opacity-60'
          }`}
          style={{
            backgroundColor: isHovered ? hoverBackground : 'transparent',
            color: isRedeeming ? COLORS.DISABLED_DARK : textColor,
            borderColor: hoverBackground,
            borderWidth: 1,
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};
